<template>
  <v-flex>
    <v-btn @click="showSearchDialog" small color="primary" class="mt-4" outlined>
      <v-icon left>search</v-icon>Buscar factura
    </v-btn>
    <v-dialog persistent width="95%" max-width="1400" v-model="dialogVisible" content-class="search-invoice-dialog">
      <v-card class="rounded-xl overflow-hidden" width="100%">
        <!-- Header con gradiente -->
        <div class="search-invoice-header px-6 py-4 d-flex align-center">
          <v-avatar color="white" size="44" class="mr-3 elevation-2">
            <v-icon color="primary" size="26">mdi-file-search-outline</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="white--text text-h6 font-weight-bold">{{ formTitle }}</div>
            <div class="white--text text-caption" style="opacity: 0.85;">
              Busca y selecciona una factura para continuar
            </div>
          </div>
          <v-btn icon dark @click="hideSearchDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-6">
          <!-- Tarjeta de filtros -->
          <v-card outlined class="rounded-lg mb-4 filter-card">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-3">
                <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2">
                  Filtros de búsqueda
                </span>
              </div>
              <v-row dense align="center">
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="searchParams.invoiceNumber"
                    label="Número de factura"
                    placeholder="Ej: FC001-0000123"
                    prepend-inner-icon="mdi-file-document-outline"
                    outlined
                    dense
                    hide-details
                    clearable
                    @keyup.enter="performSearch"
                    @click:clear="clearSearchField('invoiceNumber')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-btn
                    color="primary"
                    block
                    depressed
                    large
                    class="rounded-lg text-none font-weight-bold"
                    @click="performSearch"
                    :loading="loading"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Buscar
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Barra de resultados -->
          <div
            v-if="invoices.length > 0"
            class="d-flex align-center flex-wrap mb-3"
          >
            <v-chip color="primary" small label class="font-weight-medium mr-3">
              <v-icon small left>mdi-format-list-bulleted</v-icon>
              {{ invoices.length }}
              {{ invoices.length === 1 ? 'resultado' : 'resultados' }}
            </v-chip>
            <v-text-field
              v-model="quickSearch"
              placeholder="Filtrar resultados…"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              style="max-width: 320px;"
              class="mr-auto"
            ></v-text-field>
            <v-btn text small color="primary" class="text-none" @click="clearResults">
              <v-icon left small>mdi-refresh</v-icon>
              Limpiar
            </v-btn>
          </div>

          <!-- Tabla de resultados -->
          <v-card outlined class="rounded-lg overflow-hidden">
            <v-data-table
              :headers="headers"
              :items="invoices"
              :loading="loading"
              :search="quickSearch"
              :footer-props="{
                'items-per-page-options': [10, 20, 50],
                'items-per-page-text': 'Facturas por página',
              }"
              :no-data-text="noDataText"
              :loading-text="loadingText"
              class="search-invoice-table"
            >
              <!-- Barra de búsqueda rápida sobre los resultados -->

              <!-- Formato para montos -->
              <template v-slot:[`item.total_amount`]="{ item }">
                <span :class="getAmountColor(item.total_amount)">
                  {{ formatCurrency(item.total_amount, item.currency_code) }}
                </span>
              </template>

              <!-- Saldo disponible (total menos notas ya aplicadas) -->
              <template v-slot:[`item.available_total`]="{ item }">
                <v-chip
                  :color="availableColor(item.available_total, item.total_amount)"
                  small
                  label
                >
                  {{ formatCurrency(item.available_total, item.currency_code) }}
                </v-chip>
              </template>

              <!-- Formato para estado -->
              <template v-slot:[`item.status_id`]="{ item }">
                <v-chip
                  :color="getStatusColor(item.status_id)"
                  small
                  label
                  text-color="white"
                >
                  {{ getStatusText(item.status_id) }}
                </v-chip>
              </template>

              <template v-slot:[`item.is_ecf`]="{ item }">
                <v-chip
                  :color="item.is_ecf ? 'success' : 'grey'"
                  small
                  label
                  text-color="white"
                >
                  {{ item.is_ecf ? 'eCF' : 'No eCF' }}
                </v-chip>
              </template>

              <template v-slot:[`item.base_voucher`]="{ item }">
                <span>{{ item.base_voucher || '-' }}</span>
              </template>

              <!-- Acciones -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="selectInvoice(item)"
                    >
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>Seleccionar</span>
                </v-tooltip>
                <!-- <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="info"
                      v-bind="attrs"
                      v-on="on"
                      @click="previewInvoice(item)"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>Visualizar</span>
                </v-tooltip> -->
              </template>
            </v-data-table>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo para vista previa de factura -->
    <v-dialog v-model="previewDialog" width="80%" max-width="900">
      <v-card class="rounded-xl overflow-hidden">
        <div class="search-invoice-header px-6 py-4 d-flex align-center">
          <v-avatar color="white" size="40" class="mr-3 elevation-2">
            <v-icon color="primary">mdi-file-document-outline</v-icon>
          </v-avatar>
          <div class="white--text text-h6 font-weight-bold flex-grow-1">
            Vista previa de factura
          </div>
          <v-btn icon dark @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-4">
          <div v-if="selectedInvoice">
            <v-row>
              <v-col cols="12" md="6">
                <v-list-item two-line>
                  <v-list-item-content>
                    <v-list-item-title>Número de factura</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedInvoice.invoice_number
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="12" md="6">
                <v-list-item two-line>
                  <v-list-item-content>
                    <v-list-item-title>Cliente</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedInvoice.entity_name
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="12" md="6">
                <v-list-item two-line>
                  <v-list-item-content>
                    <v-list-item-title>Unidad de negocio</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedInvoice.business_unit_description
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="12" md="6">
                <v-list-item two-line>
                  <v-list-item-content>
                    <v-list-item-title>Total</v-list-item-title>
                    <v-list-item-subtitle>{{
                      formatCurrency(
                        selectedInvoice.total_amount,
                        selectedInvoice.currency_code
                      )
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>

              <v-col cols="12" md="6">
                <v-list-item two-line>
                  <v-list-item-content>
                    <v-list-item-title>Comprobante</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedInvoice.base_voucher || '-'
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>

              <v-col cols="12" md="6" class="d-flex align-center">
                <v-chip
                  :color="selectedInvoice.is_ecf ? 'success' : 'grey'"
                  small
                  label
                  text-color="white"
                >
                  {{ selectedInvoice.is_ecf ? 'eCF (E...)' : 'No eCF' }}
                </v-chip>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="selectInvoice(selectedInvoice)">
            Seleccionar esta factura
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import { debounce } from "lodash";

export default {
  name: "InvoiceSearch",

  props: {
    type_invoice: {
      type: Number,
      required: true,
    },
    dialogSearchInvoice: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    dialogVisible: false,
    invoices: [],
    selectedInvoice: null,
    previewDialog: false,
    loading: false,
    loadingText: "Cargando facturas...",
    noDataText: "No se encontraron facturas que coincidan con los criterios de búsqueda",

    // Parámetros de búsqueda avanzados
    searchParams: {
      invoiceNumber: "",
      entityName: "",
    },

    // Filtro rápido para resultados ya cargados
    quickSearch: "",

    headers: [
      { text: "Factura", align: "start", value: "invoice_number" },
      { text: "Unidad negocio", value: "business_unit_description" },
      { text: "Ubicación", value: "location_description" },
      { text: "Cliente", value: "entity_name" },
      { text: "Fecha", value: "date_formated" },
      { text: "Comprobante", value: "base_voucher" },
      { text: "eCF", value: "is_ecf" },
      { text: "Total", value: "total_amount", align: "end" },
      { text: "Disponible", value: "available_total", align: "end" },
      { text: "Estado", value: "status_id" },
      { text: "Acciones", value: "actions", sortable: false, align: "center" },
    ],
  }),

  computed: {
    formTitle() {
      return this.types_invoices === 1 ? "Búsqueda de Clientes" : "Búsqueda de Facturas";
    },
  },

  watch: {
    dialogSearchInvoice(val) {
      this.dialogVisible = val;
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit("hideSearch");
      }
    },
  },

  created() {
    // Inicializar el debounce para la búsqueda cuando el usuario teclea
    this.debouncedSearch = debounce(this.performSearch, 500);
  },

  methods: {
    showSearchDialog() {
      this.dialogVisible = true;
      this.$emit("showSearch");
    },

    hideSearchDialog() {
      this.dialogVisible = false;
      this.clearResults();
    },

    clearSearchField(field) {
      this.searchParams[field] = "";
      // Si ambos campos están vacíos, limpiamos los resultados
      if (!this.searchParams.invoiceNumber && !this.searchParams.entityName) {
        this.clearResults();
      }
    },

    clearResults() {
      this.invoices = [];
      this.quickSearch = "";
      this.searchParams = {
        invoiceNumber: "",
        entityName: "",
      };
    },

    async performSearch() {
      // Validar que al menos un campo de búsqueda tenga contenido
      if (!this.searchParams.invoiceNumber && !this.searchParams.entityName) {
        // Podríamos mostrar un mensaje o no hacer nada
        return;
      }

      this.loading = true;

      try {
        // Construir filtros para la consulta
        let filters =  ` AND BUSINESS_UNIT_ID in (${this.$store.state.auth.user.acess_business_unit_finance}) AND invoice_type_id IN (${this.type_invoice==1 ? '1,4' : '2,3'})`;

        // Añadir filtros condicionales basados en los parámetros de búsqueda
        const conditions = [];
        function clear_input(input) {
          return input ? input.replaceAll("'", "").trim() : "";
        }
        if (this.searchParams.invoiceNumber) {
          conditions.push(`invoice_number ilike '%${clear_input(this.searchParams.invoiceNumber)}%'`);
        }

        if (this.searchParams.entityName) {
          conditions.push(`ENTITY_NAME ilike '%${clear_input(this.searchParams.entityName)}%'`);
        }

        // Combinar condiciones con OR si hay más de una
        if (conditions.length > 0) {
          filters += ` AND (${conditions.join(" OR ")})`;
        }

        // Añadir condición de facturas registradas
        filters += " AND RECORDED=TRUE";

        // Ejecutar la consulta con saldo disponible por ítem
        const resultInvoices = await this.api_Post("income/invoice_sales_balance", {
          filters: filters,
        });

        const data = await resultInvoices.json();

        this.invoices = data.map((invoice) => {
          const baseVoucher = invoice.invoice_ncf || invoice.supplier_invoice_voucher;
          const baseVoucherStr =
            typeof baseVoucher === "string" ? baseVoucher.trim() : baseVoucher;
          const isEcf = invoice.dgii_status === 'ACEPTADO';

          const itemsBalance = invoice.items_note_balance || {};
          const available_total = parseFloat(invoice.available_total ?? invoice.total_amount ?? 0);

          // Enriquecer cada detalle de ítem con la cantidad disponible
          const enrichedDetails = (invoice.invoice_details || []).map((det) => {
            const bal = itemsBalance[String(det.item_id)];
            const creditedQty = bal ? parseFloat(bal.credited_qty || 0) : 0;
            const debitedQty  = bal ? parseFloat(bal.debited_qty  || 0) : 0;
            const availableQty = Math.max(0, parseFloat(det.quantity || 0) - creditedQty + debitedQty);
            return { ...det, available_qty: availableQty, credited_qty: creditedQty, debited_qty: debitedQty };
          });

          return {
            ...invoice,
            base_voucher: baseVoucherStr,
            is_ecf: isEcf,
            available_total,
            items_note_balance: itemsBalance,
            invoice_details: enrichedDetails,
            invoice_date: invoice.invoice_date
              ? this.formatDate(invoice.invoice_date)
              : "N/A",
          };
        });
      } catch (error) {
        console.error("Error al buscar facturas:", error);
        // Mostrar notificación de error al usuario
        this.$store.dispatch("showNotification", {
          type: "error",
          message: "Error al buscar facturas: " + error.message,
        });
        this.invoices = [];
      } finally {
        this.loading = false;
      }
    },

    selectInvoice(item) {
      this.$emit("selected-item", item);
      this.hideSearchDialog();
    },

    previewInvoice(item) {
      this.selectedInvoice = item;
      this.previewDialog = true;
    },

    // Utilidades para formato
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES");
    },

    formatCurrency(amount, currencyCode) {
      if (amount === null || amount === undefined || amount === "") return "N/A";
      const value = parseFloat(amount);
      if (isNaN(value)) return "N/A";
      const formatted = new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
      return `${formatted} ${currencyCode || ""}`.trim();
    },

    getStatusText(statusId) {
      // Mapeo de IDs de estado a textos descriptivos
      const statusMap = {
        1: "Pendiente",
        2: "Pagada",
        3: "Vencida",
        4: "Cancelada",
        // Añadir más estados según sea necesario
      };
      return statusMap[statusId] || "Desconocido";
    },

    getStatusColor(statusId) {
      // Mapeo de IDs de estado a colores
      const colorMap = {
        1: "warning",
        2: "success",
        3: "error",
        4: "grey",
        // Añadir más estados según sea necesario
      };
      return colorMap[statusId] || "primary";
    },

    getAmountColor(amount) {
      const value = parseFloat(amount);
      if (value < 0) return "red--text";
      if (value > 10000) return "green--text text--darken-2";
      return "";
    },

    availableColor(available, total) {
      const avail = parseFloat(available ?? total ?? 0);
      const orig  = parseFloat(total ?? 0);
      if (avail <= 0) return "error";
      if (avail < orig) return "warning";
      return "success";
    },
  },
};
</script>

<style scoped>
.search-invoice-dialog {
  border-radius: 16px;
}

.search-invoice-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: #fff;
}

.filter-card {
  background-color: #fafbfc !important;
  border-color: #e0e6ed !important;
}

.search-invoice-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-weight: 700 !important;
  background-color: #f5f7fa !important;
  color: #37474f !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.4px;
}

.search-invoice-table >>> .v-data-table__wrapper > table > tbody > tr {
  transition: background-color 0.15s ease;
}

.search-invoice-table >>> .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: #e3f2fd !important;
  cursor: pointer;
}

.search-invoice-table >>> .v-data-table__wrapper > table > tbody > tr > td {
  border-bottom: 1px solid #eceff1 !important;
}
</style>
